import { Buffer } from 'buffer';
import { isCapacitorShell, isMobileShell } from 'src/_shared/shell.js';
import { Auth, Cache } from 'aws-amplify';
import axios from 'axios';
import { gql } from '@apollo/client';
import getSymbolFromCurrency from 'currency-symbol-map';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import utc from 'dayjs/plugin/utc';
import * as geolib from 'geolib';
import Cookies from 'js-cookie';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import get from 'lodash/get';
import { queryReferralsByJobIdEmailPhone } from 'src/_shared/api/graphql/custom/referrals/referralsByJobIdEmailPhoneGraphql.js';
import { decryptUsingAES256 } from 'src/_shared/api/settings';
import { BONUS_STATUS, REFERRAL_STATUS } from 'src/_shared/constants/';
import { COLORS } from '../styles/colors.js';
import {
	campaignIsActive,
	getTieredBonusAmount,
	resolveActiveTieredBonus,
	tieredBonusCoversUserGroup,
} from './tieredBonus.js';

// Re-exported so existing `services/utils` imports keep working.
export {
	campaignIsActive,
	getTieredBonusAmount,
	resolveActiveTieredBonus,
	tieredBonusCoversUserGroup,
};

dayjs.extend(isBetween);
dayjs.extend(utc);
const MLRequests = [];

export const snappy = async (request) => {
	const configMode = 'prod';

	const variables = {
		...request,
		configMode,
	};
	const response = await lambda({
		endpoint: `snappy-api`,
		variables,
	});

	return response;
};

export const SSO = async (request) => {
	const configMode = 'prod';

	const variables = {
		...request,
		configMode,
	};
	const response = await lambda({
		endpoint: `sso-service-api`,
		variables,
	});

	return response;
};

export const authHeader = () => {
	const jwt = Cookies.get('jwt');
	return {
		Authorization: `Bearer ${jwt}`,
	};
};

export const normalizeDollar = (value) => {
	return value.toString().replaceAll(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const compose = (...funcs) => {
	if (funcs.length === 0) {
		return (argument) => argument;
	}

	if (funcs.length === 1) {
		return funcs[0];
	}

	return funcs.reduce(
		(a, b) =>
			(...args) =>
				a(b(...args))
	);
};

export const hasWhiteSpace = (s) => {
	return s.includes(' ');
};

export const handleAuthType = (type, reload = true) => {
	const authType = localStorage.getItem('authType');
	if (authType !== type) {
		localStorage.setItem('authType', type);
		if (reload) window.location.reload();
	}
};

export const lowerCase = (value) => {
	try {
		return typeof value === 'number'
			? value.toString().toLowerCase()
			: value.toLowerCase();
	} catch {
		return '';
	}
};

export const initialCaps = (string_) => {
	try {
		return string_
			.split(' ')
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
			.join(' ');
	} catch {
		return '';
	}
};

export const pascalCase = (value) => {
	try {
		return value.replaceAll(/(\w)(\w*)/g, function (g0, g1, g2) {
			return g1.toUpperCase() + g2.toLowerCase();
		});
	} catch {
		return value;
	}
};

export const camelCase = (value) => {
	try {
		return value
			.replaceAll(/\s(.)/g, function (a) {
				return a.toUpperCase();
			})
			.replaceAll(/\s/g, '')
			.replace(/^(.)/, function (b) {
				return b.toLowerCase();
			});
	} catch {
		return '';
	}
};

export const upperCase = (value) => {
	try {
		return typeof value === 'number'
			? value.toString().toUpperCase()
			: value.toUpperCase();
	} catch {
		return '';
	}
};

export const strip = (value) => {
	try {
		return value.replace(' ', '');
	} catch {
		return '';
	}
};

export const string = (value) => {
	try {
		return value.toString();
	} catch {
		return '';
	}
};

export const parse = (value) => {
	try {
		return JSON.parse(value);
	} catch {
		return value;
	}
};

export const decorateInternalJobLink = (link, user) => {
	if (!link) return link;
	const raw = user?.company?.whiteLabelSettings;
	if (!raw) return link;
	try {
		const settings = typeof raw === 'string' ? JSON.parse(raw) : raw;
		const suffix = settings?.internalJobLinkSuffix;
		if (!suffix) return link;
		const email = encodeURIComponent(user.emailAddress || '');
		const customValues = parse(user?.accountClaim?.customValues) || {};
		const params = suffix
			.replace(/\{email\}/g, email)
			.replace(/\{claim:([^}]+)\}/g, (_, key) =>
				encodeURIComponent(customValues?.[key.trim()] ?? '')
			)
			.replace(/^[?&]+/, '');
		if (!params) return link;
		if (/[?&]$/.test(link)) return link + params;
		return link + (link.includes('?') ? '&' : '?') + params;
	} catch {
		return link;
	}
};

export const getAddressFromLocation = (location) => {
	const address = location?.address
		? location.address
		: [location?.city, location?.state, location?.country]
				.filter(Boolean)
				.join(', ');
	return address;
};

export const getEnvironment = () => {
	let environment = 'prod';
	const host = window.location.hostname;
	if (
		host === 'localhost' ||
		host === 'referralstest.aus.com' ||
		host === 'qa.referafriend.seaworldentertainment.com' ||
		host === 'erinapp-alpha.netlify.app'
	) {
		environment = 'dev';
	}

	if (host === 'erinapp-load-test.netlify.app') {
		environment = 'load-test';
	}

	// for local prod testing
	if (String(process.env.REACT_APP_FORCE_PROD).toLowerCase() == 'true')
		environment = 'prod';

	return environment;
};

const getJwtToken = async (authType) => {
	const REFRESH_BUFFER = 60 * 1000;
	let jwtToken = null;
	let expiresAt;

	try {
		// Check if SAML login is in progress but tokens aren't ready yet
		if (localStorage.getItem('samlLoginInProgress') && !Cookies.get('jwt')) {
			throw new Error('SAML login in progress, skipping token retrieval');
		}

		if (authType === 'saml') {
			// Saml login
			const federatedToken = Cache.getItem('federatedToken');
			expiresAt = localStorage.getItem('expiresAt');

			if (federatedToken && federatedToken.id_token) {
				jwtToken = federatedToken.id_token;
				Cookies.set('jwt', jwtToken);

				if (expiresAt) {
					const currentTime = Date.now();
					if (parseInt(expiresAt) - currentTime < REFRESH_BUFFER) {
						jwtToken = await refreshJwtToken('saml');
						Cookies.set('jwt', jwtToken);
					}
				}
			} else if (federatedToken && federatedToken.refresh_token) {
				jwtToken = await refreshJwtToken('saml');
				Cookies.set('jwt', jwtToken);
			} else {
				jwtToken = (await Auth.currentSession()).getIdToken().getJwtToken();
			}
		} else {
			jwtToken = (await Auth.currentSession()).getIdToken().getJwtToken();
		}
	} catch (error) {
		console.error('Error in token retrieval:', error);
		if (authType === 'saml' && !localStorage.getItem('samlLoginInProgress')) {
			try {
				jwtToken = (await Auth.currentSession()).getIdToken().getJwtToken();
			} catch (defaultError) {
				console.error('Default auth also failed:', defaultError);
				throw defaultError;
			}
		} else {
			throw error;
		}
	}

	return { jwtToken, expiresAt };
};

const refreshJwtToken = async (authType) => {
	try {
		if (authType === 'default') {
			const session = await Auth.currentSession();
			const token = session.getIdToken().getJwtToken();
			Cookies.set('jwt', token);
			return token;
		} else {
			try {
				const federatedToken = Cache.getItem('federatedToken');
				if (!federatedToken || !federatedToken.refresh_token) {
					console.log(
						'No federated token or refresh token found, redirecting to logout'
					);
					window.location.href = `${window.location.origin}/logout`;
				}

				const refreshToken = federatedToken.refresh_token;
				const domain = Cache.getItem('federatedDomain');
				const region = Cache.getItem('federatedRegion');
				const clientId = Cache.getItem('federatedClientId');
				const clientSecret = Cache.getItem('federatedClientSecret');

				if (!domain || !region || !clientId || !clientSecret) {
					console.error('Missing required federation parameters');
					throw new Error('Missing federation parameters');
				}

				const scope =
					'aws.cognito.signin.user.admin+email+openid+phone+profile';
				const body = `client_id=${clientId}&client_secret=${clientSecret}&grant_type=refresh_token&refresh_token=${refreshToken}&scope=${scope}`;
				const url = `https://${domain}.auth.${region}.amazoncognito.com/oauth2/token`;

				const response = await axios.post(url, body, {
					headers: {
						'Content-Type': 'application/x-www-form-urlencoded',
						Authorization:
							'Basic ' + window.btoa(clientId + ':' + clientSecret),
					},
				});

				const tokens = response.data;

				if (!tokens.refresh_token && federatedToken.refresh_token) {
					tokens.refresh_token = federatedToken.refresh_token;
				}

				Cache.setItem('federatedToken', tokens);

				const token = tokens.id_token;
				Cookies.set('jwt', token);

				const expiresAt = Date.now() + tokens.expires_in * 1000;
				localStorage.setItem('expiresAt', expiresAt.toString());

				return token;
			} catch (error) {
				console.error('Error refreshing federated token:', error);
				throw error;
			}
		}
	} catch (error) {
		console.log(error);
		// Don't auto-logout if SAML login is in progress
		if (!localStorage.getItem('samlLoginInProgress')) {
			window.location.href = `${window.location.origin}/logout`;
		}
	}
};

// Used in employee page to filter results via the search component
export const searchInviteData = (properties = [], query = '', dataSet = []) => {
	return dataSet.filter((item) =>
		properties.some((property) =>
			lowerCase(get(item, [property], [])).includes(lowerCase(query.trim()))
		)
	);
};

export const searchNetwork = async (query, currentUser) => {
	const configMode = getEnvironment() === 'dev' ? 'DEV' : 'PROD';
	const elasticquery = new Object({
		request: query,
		type: 'job',
		currentUser,
		configMode,
	});
	return lambda({
		endpoint: 'elasticdatacalls-prod-api',
		variables: elasticquery,
	}).then((response) => response.results);
};

export const searchOpenSearchNetwork = async (request, type) => {
	let configMode = getEnvironment();
	if (configMode === 'other') {
		configMode = 'prod';
	}

	request = request?.request ? request.request : request;
	if (typeof request?.query === 'object') {
		request = request?.query?.request;
	}

	const variables = {
		request,
		type,
		currentUser: request?.currentUser?.id
			? request.currentUser.id
			: request?.currentUser,
		configMode,
	};
	const response = await lambda({
		endpoint: `open-search-api-${configMode}`,
		variables,
	});

	return response;
};

const searchGDPRData = (props) => {
	const query = get(props, 'query');
	const dataSet = get(props, 'gdprReferrals', []);
	const searchQuery = lowerCase(query.split(' ').join(''));
	let totalFilters = 0;
	for (const filter of Object.entries(get(props, 'filters', {}))) {
		if (typeof filter[1] === 'object' && filter[1].length > 0) totalFilters++;
	}

	const results = dataSet.filter((item) => {
		if ((!searchQuery || searchQuery.length === 0) && totalFilters === 0) {
			return item;
		}

		let matchesQuery = false;
		let matchesFilters = false;
		if (totalFilters > 0) {
			let filtersMatched = 0;
			for (const filter of Object.entries(get(props, 'filters', {}))) {
				if (typeof filter[1] === 'object' && filter[1].length > 0) {
					if (filter[0] === 'bonuses') {
						let isMatch = false;
						for (const bonus of filter[1]) {
							if (bonus === get(item, 'job.referralBonus.tieredBonusId'))
								isMatch = true;
						}

						if (isMatch) filtersMatched++;
					}

					if (filter[0] === 'departments') {
						let isMatch = false;
						for (const department of filter[1]) {
							if (get(department, 'id') === get(item, 'job.departmentId'))
								isMatch = true;
						}

						if (isMatch) filtersMatched++;
					}

					if (filter[0] === 'recruiters') {
						let isMatch = false;
						for (const recruiter of filter[1]) {
							if (recruiter === get(item, 'job.hiringManagerId'))
								isMatch = true;
						}

						if (isMatch) filtersMatched++;
					}

					if (filter[0] === 'statuses') {
						let isMatch = false;
						for (const status of filter[1]) {
							if (status === get(item, 'status')) isMatch = true;
						}

						if (isMatch) filtersMatched++;
					}

					if (filter[0] === 'subCompanies') {
						let isMatch = false;
						for (const subCompany of filter[1]) {
							if (subCompany === get(item, 'job.subCompanyId')) isMatch = true;
						}

						if (isMatch) filtersMatched++;
					}

					if (totalFilters === filtersMatched) matchesFilters = true;
				}
			}
		} else {
			matchesFilters = true;
		}

		if (searchQuery.length > 0) {
			if (lowerCase(get(item, 'job.title')).includes(lowerCase(query)))
				matchesQuery = true;
			if (
				lowerCase(
					get(item, 'user.firstName', '') + get(item, 'user.lastName', '')
				).includes(lowerCase(searchQuery))
			)
				matchesQuery = true;
			if (
				lowerCase(get(item, 'user.emailAddress')).includes(
					lowerCase(searchQuery)
				)
			)
				matchesQuery = true;
			if (lowerCase(get(item, 'user.employeeId')).includes(lowerCase(query)))
				matchesQuery = true;
		} else {
			matchesQuery = true;
		}

		if (matchesFilters && matchesQuery) return item;
	});
	return results;
};

export const searchReferralsNetwork = async (query, currentUser) => {
	const configMode = getEnvironment() === 'dev' ? 'DEV' : 'PROD';
	const elasticquery = new Object({
		request: query,
		type: 'referral',
		currentUser,
		configMode,
	});
	return lambda({
		endpoint: 'elasticdatacalls-prod-api',
		variables: elasticquery,
	}).then((response) => response.results);
};

export const searchReferralsDataNetwork = async (props) => {
	let stages = [];
	if (get(props, 'currentUser.company.stages')) {
		stages = parse(get(props, 'currentUser.company.stages', [])).map(
			(stage) => Object.values(stage)[0]
		);
	}

	let qry = get(props, 'query', '');
	const gdprReferrals = get(props, 'gdprReferrals');
	let gdpr;
	if (gdprReferrals) gdpr = searchGDPRData(props);
	if (get(props, 'query', '').includes('@')) qry = `"${get(props, 'query')}"`;

	const query = {
		query: qry,
		filters: {
			all: [
				{
					company_id: get(props, 'filters.companies'),
				},
			],
			any: [],
			none: [],
		},
		page: {
			size: 300,
		},
	};

	if (get(props, 'filters.referralType')) {
		query.filters.all.push({ referral_type: 'self' });
	}

	if (get(props, 'filters.bonuses', []).length > 0) {
		const filters = get(query, 'filters.all');
		const bonuses = [];
		for (const b of get(props, 'filters.bonuses', [])) bonuses.push(b);
		const newFilter = {
			tiered_bonus_id: bonuses,
		};
		filters.push(newFilter);
		query.filters.all = filters;
	}

	if (get(props, 'filters.customStatuses', []).length > 0) {
		const swiftypeAnyFilters = get(query, 'filters.any');
		const companyStageFilters = get(props, 'filters.customStatuses', ['']);
		query.filters.any = [
			...swiftypeAnyFilters,
			{
				all: [
					{ custom_status: companyStageFilters },
					{ status: ['interviewing'] },
				],
			},
		];
	}

	if (get(props, 'filters.departments', []).length > 0) {
		const filters = get(query, 'filters.all');
		const departments = [];
		for (const d of get(props, 'filters.departments', []))
			departments.push(d.id);
		const newFilter = {
			department_id: departments,
		};
		filters.push(newFilter);
		query.filters.all = filters;
	}

	if (get(props, 'filters.recruiters', []).length > 0) {
		const filters = get(query, 'filters.all');
		const recruiters = [];
		for (const r of get(props, 'filters.recruiters', [])) recruiters.push(r);
		const newFilter = {
			hiring_manager_id: recruiters,
		};
		filters.push(newFilter);
		query.filters.all = filters;
	}

	if (get(props, 'filters.statuses', []).length > 0) {
		const stageFilters = get(props, 'filters.statuses', []);
		const swiftypeAnyFilters = get(query, 'filters.any');
		const customStatus = get(props, 'filters.customStatuses', []);
		let noneFilter = [];

		if (customStatus.length > 0) {
			noneFilter = [{ custom_status: [''] }];
		}

		if (stageFilters.includes('interviewing')) {
			query.filters.any = [
				...swiftypeAnyFilters,
				{
					all: [{ status: stageFilters }, { none: noneFilter }],
				},
			];
		} else {
			query.filters.any = [
				...swiftypeAnyFilters,
				{
					all: [{ status: stageFilters }],
				},
			];
		}
	}

	if (get(props, 'filters.subCompanies', []).length > 0) {
		const filters = get(query, 'filters.all');
		const subCompanies = [];
		for (const sc of get(props, 'filters.subCompanies', []))
			subCompanies.push(sc);
		const newFilter = {
			sub_company_id: subCompanies,
		};
		filters.push(newFilter);
		query.filters.all = filters;
	}

	if (get(props, 'filters.userGroups', []).length > 0) {
		const filters = get(query, 'filters.all');
		const userGroups = [];
		for (const userGroup of get(props, 'filters.userGroups', []))
			userGroups.push(userGroup.id);
		const newFilter = {
			user_group_id: userGroups,
		};
		filters.push(newFilter);
		query.filters.all = filters;
	}

	const currentUserId = get(props, 'currentUser.id');
	const matchedReferrals = await searchReferralsNetwork(query, currentUserId);
	const allData = [];
	if (gdpr) for (const ref of gdpr) allData.push(ref);

	for (const ref of matchedReferrals) {
		const resume = parse(get(ref, 'contact_resume.raw', '{}'));
		const contactResume = get(resume, 'key') ? resume : null;
		const newReference = {
			companyId: get(ref, 'company_id.raw'),
			contact: {
				id: get(ref, 'contact_id.raw'),
				firstName: get(ref, 'contact_first_name.raw'),
				lastName: get(ref, 'contact_last_name.raw'),
				employeeId: get(ref, 'contact_employee_id.raw'),
				emailAddress: get(ref, 'contact_email_address.raw'),
				phoneNumber: get(ref, 'contact_phone_number.raw'),
			},
			contactId: get(ref, 'contact_id.raw'),
			contactResume,
			customStatus: get(ref, 'custom_status.raw'),
			diversityHire: get(ref, 'diversity_hire.raw') === 'true',
			hireDate: get(ref, 'date_hired.raw'),
			id: get(ref, 'id.raw'),
			job: {
				id: get(ref, 'job_id.raw'),
				title: get(ref, 'job_title.raw'),
				hiringManager: get(ref, 'hiring_manager_id.raw'),
				tieredBonusId: get(ref, 'tiered_bonus_id.raw'),
				tieredBonus: {
					id: get(ref, 'tiered_bonus_id.raw'),
					name: get(ref, 'tiered_bonus_name.raw'),
					tiers: get(ref, 'tiered_bonus_tiers.raw'),
				},
				subCompanyId: get(ref, 'sub_company_id'),
				externalJobId: get(ref, 'job_external_id.raw'),
			},
			bonus: {
				amount: get(ref, 'bonus_amount.raw'),
				tieredBonusId: get(ref, 'tiered_bonus_id.raw'),
			},
			jobId: get(ref, 'job_id.raw'),
			message: get(ref, 'message.raw', null),
			note: get(ref, 'note.raw'),
			referralDate: get(ref, 'date_referred.raw'),
			referralType: get(ref, 'referral_type.raw'),
			status: get(ref, 'status.raw'),
			user: {
				id: get(ref, 'user_id.raw'),
				firstName: get(ref, 'user_first_name.raw'),
				lastName: get(ref, 'user_last_name.raw'),
				employeeId: get(ref, 'contact_employee_id.raw'),
				userGroupId: get(ref, 'user_group_id.raw'),
			},
			userId: get(ref, 'user_id.raw'),
			referralSource: get(ref, 'referral_source.raw'),
		};

		if (get(ref, 'retro_tiered_bonus_id.raw')) {
			newReference.tieredBonusId = get(ref, 'retro_tiered_bonus_id.raw');
			const retroTieredBonus = {
				id: get(ref, 'retro_tiered_bonus_id.raw'),
				name: get(ref, 'retro_tiered_bonus_name.raw'),
				tiers: get(ref, 'retro_tiered_bonus_tiers.raw'),
			};
			newReference.tieredBonus = retroTieredBonus;
		}

		if (get(ref, 'campaign_id.raw')) {
			newReference.campaignId = get(ref, 'campaign_id.raw');
			newReference.campaign = {
				id: get(ref, 'campaign_id.raw'),
				archived: get(ref, 'campaign_archived.raw'),
				name: get(ref, 'campaign_name.raw'),
				startDate: get(ref, 'campaign_start_date.raw'),
				endDate: get(ref, 'campaign_end_date.raw'),
				tieredBonusId: get(ref, 'campaign_tiered_bonus_id.raw'),
				tieredBonus: {
					id: get(ref, 'campaign_tiered_bonus_id.raw'),
					name: get(ref, 'campaign_tiered_bonus_name.raw'),
					tiers: get(ref, 'campaign_tiered_bonus_tiers.raw'),
				},
			};
		}

		allData.push(newReference);
	}

	const data = allData;
	let totals = {};
	if (get(props, 'filterOn') && data.length < 1000) {
		totals.total = data.length;
		totals.accepted = data.filter(
			(r) => get(r, 'status') === 'accepted'
		).length;
		totals.interviewing = data.filter(
			(r) => get(r, 'status') === 'interviewing'
		).length;
		totals.hired = data.filter((r) => get(r, 'status') === 'hired').length;
		totals.declined = data.filter(
			(r) => get(r, 'status') === 'declined'
		).length;
		totals.noResponse = data.filter(
			(r) => get(r, 'status') === 'noresponse'
		).length;
		totals.inactive = data.filter(
			(r) => get(r, 'status') === 'inactive'
		).length;
		totals.notHired = data.filter(
			(r) => get(r, 'status') === 'notHired'
		).length;
	} else {
		totals = get(props, 'totals');
	}

	return { data, totals, query: get(props, 'query', '') };
};

export const filterByDistance = async (
	dataSet = [],
	filterDistance,
	lat,
	lng
) => {
	if (filterDistance < 1 || !lat || !lng) return dataSet;

	// Handle async filtering by creating map of true/false promise results
	const resolved = await Promise.all(
		dataSet.map(async (entry) => {
			const locationCoordinates = [
				...(typeof entry?.location === 'string'
					? entry.location.split(',')
					: [
							entry?.location?.coordinates?.lat,
							entry?.location?.coordinates?.lon,
						]),
			];
			let latitude =
				entry?.lat ??
				locationCoordinates[0] ??
				entry?.location?.lat ??
				entry?.job?.location?.lat;
			let longitude =
				entry?.lng ??
				locationCoordinates[1] ??
				entry?.location?.lng ??
				entry?.location?.lon ??
				entry?.job?.location?.lng;
			if (!latitude || !longitude) {
				const { lat, lng } = (await getCoordinates(entry?.job?.location)) ?? {
					lat: undefined,
					lng: undefined,
				};
				latitude = lat;
				longitude = lng;
			}

			if (latitude && longitude && lat && lng) {
				const isInRadius = geolib.isPointWithinRadius(
					{
						latitude: lat,
						longitude: lng,
					},
					{ latitude, longitude },
					filterDistance * 1609.344
				);
				return isInRadius;
			}

			return false;
		})
	);

	// Filter dataset by true/false map
	return dataSet.filter((job, i) => resolved[i]);
};

export const validEmail = (inputText) => {
	try {
		const mailformat =
			/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[(?:\d{1,3}\.){3}\d{1,3}])|(([a-zA-Z\-\d]+\.)+[a-zA-Z]{2,}))$/;
		return inputText.match(mailformat);
	} catch {
		return false;
	}
};

export const getCoordinates = async (location = '{}', auth = 'cognito') => {
	try {
		location = typeof location === 'string' ? parse(location) : location;
		const variables = {
			type: 'getCoordinates',
			...location,
		};
		const response = await lambda(
			{
				endpoint: 'location-services-api',
				variables,
			},
			auth
		);
		const coordinates = response?.lat
			? { lat: response?.lat, lng: response?.lon }
			: null;
		return coordinates;
	} catch (error) {
		console.log(error);
	}
};

export const placesAutocomplete = async (address, auth = 'cognito') => {
	try {
		const variables = {
			type: 'autocomplete',
			address,
		};
		const response = await lambda(
			{
				endpoint: 'location-services-api',
				variables,
			},
			auth
		);
		return response;
	} catch (error) {
		console.log(error);
	}
};

export const getLocation = async (props, auth = 'cognito') => {
	try {
		const variables = {
			type: props?.place ? 'getPlace' : 'getLocation',
			...props,
		};
		const response = await lambda(
			{
				endpoint: 'location-services-api',
				variables,
			},
			auth
		);
		return response;
	} catch (error) {
		console.log(error);
	}
};

export const findAddressComponent = (
	type,
	addrComponents,
	length_ = 'long_name'
) => {
	let result = null;
	for (const comp of addrComponents) {
		if (get(comp, 'types', []).find((t) => t === type)) result = comp;
	}

	return result ? result[length_] : result;
};

export const searchData = (properties, query = '', dataSet) => {
	const searchName = lowerCase(query.replaceAll(/\s+/g, ''));
	return dataSet.filter(
		(item) =>
			!searchName ||
			properties.some(
				(property) =>
					(item[property] &&
						lowerCase(item[property].replaceAll(/\s+/g, '')).includes(
							searchName
						)) ||
					(property === 'phoneNumber' && item[property] != null
						? item[property].replaceAll(/[^a-z\d]/gi, '').includes(searchName)
						: '')
			) ||
			(item.firstName &&
				item.lastName &&
				lowerCase(
					(item.firstName + item.lastName).replaceAll(/\s+/g, '')
				).includes(searchName))
	);
};

export const searchJobsData = (properties, dataSet) => {
	return dataSet.filter(
		(item) => item.title && lowerCase(item.title).includes(properties)
	);
};

export const searchReferralData = (properties, query = '', dataSet) => {
	const searchName = lowerCase(query.split(' ').join(''));
	const searchByTitle = lowerCase(query);
	return dataSet.filter(
		(item) =>
			!searchName ||
			(item.contact &&
				lowerCase(
					get(item, "contact['firstName']", '') +
						get(item, "contact['lastName']", '')
				).includes(searchName)) ||
			(item.job &&
				lowerCase(get(item, "job['title']", '')).includes(searchByTitle))
	);
};

export const searchBonusData = (query, dataSet, filters) => {
	const searchByName = lowerCase(query.split(' ').join(''));
	const searchByTitle = lowerCase(query);

	const isFilteredByDates = get(filters, 'dates', []).length > 0;
	const isFilteredByStatus = get(filters, 'statuses', []).length > 0;
	const isFilteredByUserGroups = get(filters, 'userGroups', []).length > 0;

	const bonuses = [...dataSet];
	const dateRangeFilters = get(filters, 'dates', []);
	const referralIdFilter = get(filters, 'referralId');
	const statusFilters = get(filters, 'statuses', []);
	const userGroupFilters = get(filters, 'userGroups', []);

	return bonuses.filter((currentBonus) => {
		const userName =
			get(currentBonus, 'user.firstName') + get(currentBonus, 'user.lastName');
		const contactName =
			get(currentBonus, 'contact.firstName') +
			get(currentBonus, 'contact.lastName');
		let result = null;
		const jobTitle = get(currentBonus, 'job.title');

		if (!searchByName || searchByName === '') result = currentBonus;
		if (lowerCase(userName).includes(searchByName)) result = currentBonus;
		if (lowerCase(contactName).includes(searchByName)) result = currentBonus;
		if (lowerCase(jobTitle).includes(searchByTitle)) result = currentBonus;
		if (
			referralIdFilter &&
			get(currentBonus, 'referralId') === referralIdFilter
		)
			result = currentBonus;

		if (result && isFilteredByDates) {
			let match = false;
			const start = dayjs(dateRangeFilters[0]).subtract(1, 'day');
			const end = dayjs(dateRangeFilters[1]).subtract(1, 'day');

			if (
				dayjs(get(currentBonus, 'earnedDate')).isBetween(
					start,
					end,
					'days',
					'[]'
				)
			) {
				match = true;
			}

			if (!match) result = null;
		}

		if (result && isFilteredByStatus) {
			let match = false;

			for (const currentStatus of statusFilters) {
				let bonusStatus = get(currentBonus, 'bonusStatus');

				if (bonusStatus === 'earned') bonusStatus = 'eligible';
				if (
					bonusStatus !== 'pending' &&
					bonusStatus !== 'paid' &&
					bonusStatus !== 'eligible' &&
					bonusStatus !== 'needsReview'
				) {
					bonusStatus = 'ineligible';
				}

				if (currentStatus === bonusStatus) match = true;
			}

			if (!match) result = null;
		}

		if (result && isFilteredByUserGroups) {
			let match = false;

			for (const currentUserGroup of userGroupFilters) {
				const selectedUserGroup = get(currentBonus, 'user.userGroup.name');

				if (selectedUserGroup === currentUserGroup) match = true;
			}

			if (!match) result = null;
		}

		if (result) return result;
	});
};

export const findWithAttr = (array, attribute, value) => {
	for (const [i, element] of array.entries()) {
		if (element[attribute] === value) {
			return i;
		}
	}

	return -1;
};

export const capitalizeFirstLetter = (string = '') => {
	try {
		return string.charAt(0).toUpperCase() + string.slice(1);
	} catch {
		return string;
	}
};

export const calculateReferralBonus = (
	contactIncentiveBonus,
	referralBonusAmount,
	eligible,
	tieredBonus,
	tieredBonusType,
	userGroupId,
	hireDate
) => {
	const programActive = contactIncentiveBonus > 0;
	if (tieredBonus) {
		return getTieredBonusAmount(
			tieredBonus,
			tieredBonusType,
			userGroupId,
			hireDate
		);
	}

	if (
		!referralBonusAmount ||
		referralBonusAmount === 0 ||
		referralBonusAmount === null
	) {
		return 0;
	}

	if (!programActive) {
		return Number.parseInt(referralBonusAmount);
	}

	if (programActive && eligible) {
		return Number.parseInt(referralBonusAmount);
	}

	if (programActive && !eligible) {
		return Number.parseInt(
			Math.max(0, referralBonusAmount - contactIncentiveBonus)
		);
	}
};

export const calculateBonus = (job, user, options) => {
	const contactIncentiveBonus = job?.company?.contactIncentiveBonus || 0;
	const referralBonus = parse(job?.referralBonus);
	const referralBonusAmount = referralBonus?.amount;
	const eligible = user?.incentiveEligible || false;
	const programActive = contactIncentiveBonus > 0;
	const userGroupId = user?.userGroupId;
	const recipient = options?.recipient;
	const hireDate = options?.hireDate;
	const tieredBonus = resolveActiveTieredBonus(job, userGroupId);

	if (tieredBonus && recipient) {
		return getTieredBonusAmount(tieredBonus, recipient, userGroupId, hireDate);
	}

	if (tieredBonus) {
		return tieredBonus?.name;
	}

	if (
		!referralBonusAmount ||
		referralBonusAmount === 0 ||
		referralBonusAmount === null
	) {
		return 0;
	}

	if (!programActive) {
		return Number.parseInt(referralBonusAmount);
	}

	if (programActive && eligible) {
		return Number.parseInt(referralBonusAmount);
	}

	if (programActive && !eligible) {
		return Number.parseInt(
			Math.max(0, referralBonusAmount - contactIncentiveBonus)
		);
	}
};

export const getBonusAmount = (job, user, options) => {
	const bonusValue = calculateBonus(job, user, options);
	let currencyCode = user?.userGroup?.currency;
	if (currencyCode === null) currencyCode = 'USD';
	const symbol = getSymbolFromCurrency(currencyCode);
	if (typeof bonusValue === 'string') {
		return bonusValue;
	}

	if (bonusValue > 0) {
		return formatBonusAmount(symbol, bonusValue);
	}

	return 'None';
};

export const formatBonusAmount = (symbol, bonusValue) => {
	return `${symbol}${bonusValue}`.replaceAll(
		/(?<=\d)\B(?=(\d{3})+(?!\d))/g,
		','
	);
};

export const calculateReferralBonusAllReferrals = (
	contactIncentiveBonus,
	referralBonusAmount,
	eligible,
	tieredBonus,
	tieredBonusType,
	userGroupId,
	hireDate
) => {
	const programActive = contactIncentiveBonus > 0;

	if (tieredBonus) {
		return getTieredBonusAmountAllReferrals(
			tieredBonus,
			tieredBonusType,
			userGroupId,
			hireDate
		);
	}

	if (!referralBonusAmount || referralBonusAmount === 0) {
		('');
		return 0;
	}

	if (!programActive) {
		return Number.parseInt(referralBonusAmount);
	}

	if (programActive && eligible) {
		return Number.parseInt(referralBonusAmount);
	}

	if (programActive && !eligible) {
		return Number.parseInt(
			Math.max(0, referralBonusAmount - contactIncentiveBonus)
		);
	}
};

export const getTieredBonusAmountAllReferrals = (
	tieredBonus,
	tieredBonusType,
	userGroupId,
	hireDate
) => {
	return tieredBonus.tiers.reduce((total, tier) => {
		tier = typeof tier === 'string' ? parse(tier) : tier;
		if (
			(!tieredBonusType ||
				(tier.recipientType &&
					lowerCase(tier.recipientType) === lowerCase(tieredBonusType))) && // If hireDate is included only include eligible dates in total
			get(tier, 'userGroup') === userGroupId
		)
			total += Number.parseInt(get(tier, 'amount'), 10);
		return total;
	}, 0);
};

export const calculateReferralBonusTotal = (
	referrals,
	eligible,
	incentiveBonus
) => {
	let total = 0;
	for (const referral of referrals) {
		const { job } = referral;
		const referralBonus = get(referral, 'job.referralBonus');
		const amount =
			typeof referralBonus === 'object'
				? get(referralBonus, 'amount')
				: typeof referralBonus === 'string'
					? parse(referralBonus).amount
					: 0;

		if (
			referral.status === 'hired' &&
			job &&
			amount &&
			referral.referralType !== 'self'
		) {
			const bonusAmount = calculateReferralBonus(
				incentiveBonus,
				amount,
				eligible,
				'',
				'',
				''
			);

			total += Number.parseInt(bonusAmount, 10);
		}
	}

	return total.toString().replaceAll(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const calculateTotalBonuses = (bonuses, eligible) => {
	let total = 0;
	if (eligible) {
		for (const bonus of bonuses) {
			if (
				bonus.recipientType === 'employee' &&
				(bonus.bonusStatus === 'earned' || bonus.bonusStatus === 'paid')
			) {
				const amount = get(bonus, 'amountDue', 0);
				total += amount;
			}
		}
		// Values for myReferrals page
	} else {
		for (const bonus of bonuses) {
			if (bonus.recipientType === 'employee') {
				const amount = get(bonus, 'amountDue', 0);
				total += amount;
			}
		}
	}

	return total.toString().replaceAll(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const calculateTotalBonusesDashboard = (bonuses, eligible) => {
	let total = 0;
	if (eligible) {
		for (const bonus of bonuses) {
			if (
				bonus.recipientType === 'employee' &&
				(bonus.bonusStatus === 'earned' ||
					bonus.bonusStatus === 'paid' ||
					bonus.bonusStatus === 'pending')
			) {
				const amount = get(bonus, 'amountDue', 0);
				total += amount;
			}
		}
		// Values for myReferrals page
	} else {
		for (const bonus of bonuses) {
			if (bonus.recipientType === 'employee') {
				const amount = get(bonus, 'amountDue', 0);
				total += amount;
			}
		}
	}

	return total.toString().replaceAll(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const formatPhoneNumber = (number) => {
	if (typeof number !== 'string') return;

	let filteredNumber = number.replaceAll(/\D/g, '');
	if (filteredNumber.length >= 11) {
		filteredNumber = '+' + filteredNumber;
	} else if (filteredNumber.length <= 10) {
		filteredNumber = '+1' + filteredNumber;
	}

	const phone = parsePhoneNumberFromString(filteredNumber);

	const numberFormat =
		get(phone, 'country', []) === 'US' ? 'NATIONAL' : 'INTERNATIONAL';

	let formattedPhoneNumber = null;
	if (phone) {
		try {
			formattedPhoneNumber = phone.format(numberFormat);
		} catch (error) {
			console.error(error);
		}
	} else {
		// Skip invalid phone numbers
	}

	return formattedPhoneNumber;
};

export const filterPhoneNumber = (number) => {
	if (typeof number !== 'string') return;

	let filteredNumber = number.replaceAll(/\D/g, '');
	if (filteredNumber.length >= 11) {
		filteredNumber = '+' + filteredNumber;
	} else if (filteredNumber.length <= 10) {
		filteredNumber = '+1' + filteredNumber;
	}

	return filteredNumber;
};

// Determines whether a contact (identified by email and/or phone) has already
// been referred to a given job. Uses the backend queryReferralsByJobIdEmailPhone
// index, which normalizes/trims the email and phone server-side and matches on
// the contact tied to each referral. The query accepts emailAddress OR
// phoneNumber per call (not both), so we look up each identifier we have and
// block if either already has a referral on this job.
//
// Query errors are intentionally not swallowed here — callers wrap this in their
// own try/catch so they can decide how to handle a transient failure. The
// referral flows log and let the submission proceed rather than blocking it.
export const isAlreadyReferredToJob = async (
	client,
	{ jobId, emailAddress, phoneNumber }
) => {
	if (!jobId) return false;

	// Contacts are saved with the raw (sanitized) phone, so normalize to the same
	// shape filterPhoneNumber produces elsewhere before handing it to the backend.
	const normalizedPhone = phoneNumber ? filterPhoneNumber(phoneNumber) : null;

	const lookups = [];
	if (emailAddress) lookups.push({ emailAddress, phoneNumber: null });
	if (normalizedPhone)
		lookups.push({ emailAddress: null, phoneNumber: normalizedPhone });

	for (const variables of lookups) {
		// eslint-disable-next-line no-await-in-loop
		const { data } = await client.query({
			query: gql(queryReferralsByJobIdEmailPhone),
			variables: { jobId, ...variables },
			fetchPolicy: 'network-only',
		});
		const items = get(data, 'queryReferralsByJobIdEmailPhone.items', []);
		if (items && items.length > 0) return true;
	}

	return false;
};

export const downloadFromS3Signed = async (key, bucket = 'erin-avatars') => {
	if (!key) {
		return;
	}

	const auth = bucket === 'erin-images' ? 'apiKey' : 'cognito';

	try {
		const result = await lambda(
			{ endpoint: 's3-signer', variables: { bucket, key } },
			auth
		);
		return result.signedUrl;
	} catch (error) {
		console.log(error);
	}
};

export const downloadFromS3 = (key, bucket = 'erin-images') => {
	let s3url = `https://${bucket}.s3.us-east-2.amazonaws.com/${key}`;
	s3url = s3url.split(' ').join('+');
	return s3url;
};

export const uploadToS3 = async (
	file,
	key,
	bucket = 'erin-images',
	contentType = null
) => {
	const result = await lambda({
		endpoint: 's3-signer',
		variables: { bucket, key, operation: 'put' },
	});
	const url = result.signedUrl;

	await axios.put(url, file, {
		headers: {
			'Content-Type': file.type,
		},
	});
};

export const uploadImageToS3FromHugeRTE = async (
	image,
	file,
	key,
	bucket = 'erin-avatars'
) => {
	return new Promise(async (resolve, reject) => {
		try {
			const result = await lambda({
				endpoint: 's3-signer',
				variables: { bucket, key, operation: 'put' },
			});
			const url = result.signedUrl;
			const buf = Buffer.from(
				image.replace(/^data:image\/\w+;base64,/, ''),
				'base64'
			);

			await axios.put(url, buf, {
				headers: {
					'Content-Type': file.type,
					'Content-Encoding': 'base64',
				},
			});

			resolve({
				bucket,
				key,
				region: 'us-east-2',
			});
		} catch (error) {
			reject(error);
		}
	});
};

export const ml = (query, user, languageData = [], module) => {
	try {
		if (languageData.length > 0) {
			const languageCode = get(user, 'languageCode');
			if (languageCode === 'US' || !languageCode) return query;
			let result = query;
			const matches = languageData.filter((data) => {
				const module_ = module ? get(data, 'module') === module : true;
				return (
					get(data, 'languageCode') === languageCode &&
					get(data, 'matchKey') === query &&
					module_
				);
			});
			const match = matches && matches.length > 0 ? matches[0] : null;
			if (match) {
				result = match?.text;
			} else {
				if (
					typeof languageCode === 'string' &&
					(languageCode.length === 2 ||
						(languageCode.length === 5 && languageCode.includes('-')))
				) {
					try {
						sendToMultiLingualTranslationManager(
							query,
							languageCode,
							module,
							languageData
						);
					} catch (error) {
						console.log(error);
					}

					return query;
				}

				return result;
			}

			return result;
		}

		return query;
	} catch (error) {
		console.log(error);
	}
};

const sendToMultiLingualTranslationManager = async (
	key,
	languageCode,
	module,
	languageData
) => {
	try {
		if (
			!MLRequests.find((request) => request.key === key) &&
			languageData.find((item) => item.languageCode === languageCode)
		) {
			const variables = {
				key,
				languageCode,
				module,
				path: getPath(),
			};
			MLRequests.push(variables);
			console.log('No Translation Result:', key, languageCode, module);
			const response = await lambda({
				endpoint: 'multi-lingual-translation-manager',
				variables,
			});
			if (response) {
				languageData.push(response);
				// Dynamic import to fix circular dependency
				await import('src/actions.js').then((actions) => {
					const { dashboardActions } = actions;
					dashboardActions.createSetMultiLingualData(languageData);
				});
			}

			return response?.text || query;
		}
	} catch (error) {
		console.log(error);
	}
};

const getPath = () => {
	try {
		let path = window.location.pathname;
		path = path.split('/')[1];
		return path;
	} catch {
		return null;
	}
};

export const isCurrentCampaign = (campaign, referralDate) => {
	if (!campaign || get(campaign, 'archived') === true) return false;
	const startDate = new Date(get(campaign, 'startDate')).getTime();
	const endDate = new Date(get(campaign, 'endDate')).getTime();
	const today = new Date(referralDate).getTime();
	return today - startDate >= 0 && today - endDate <= 0;
};

export const formatDate = (date, languageCode, dateFormat = null) => {
	if (typeof date === 'string') {
		try {
			const dateArray = date.split('T')[0].split('-');
			const day =
				dateArray[2].split('')[0] === '0'
					? dateArray[2].split('')[1]
					: dateArray[2];
			const month =
				dateArray[1].split('')[0] === '0'
					? dateArray[1].split('')[1]
					: dateArray[1];
			const year = dateArray[0];
			if (
				languageCode === 'FR' ||
				languageCode === 'DE' ||
				languageCode === 'ES' ||
				languageCode === 'RU' ||
				dateFormat === 'European'
			) {
				return `${day}/${month}/${year}`;
			}

			return `${month}/${day}/${year}`;
		} catch {
			return null;
		}
	} else {
		return null;
	}
};

export const translation = async (query, user) => {
	const languageCode = user?.languageCode;
	const response = await lambda(
		{
			endpoint: 'multi-lingual-translation-api',
			variables: {
				key: query,
				languageCode,
			},
		},
		'apiKey'
	);
	return response?.TranslatedText || query;
};

export const findItemByNameAndKeywords = (name, values = [], options) => {
	try {
		if (values !== undefined && values.length > 0) {
			values = values.map((value) => {
				if (value?.keywords && typeof value.keywords === 'string') {
					// Create a new object instead of modifying the existing one
					return {
						...value,
						keywords: JSON.parse(value.keywords),
					};
				}

				return value;
			});
		}

		const result = values.find((val) => {
			let keywords = val?.keywords || [];
			if (!keywords || keywords == null) keywords = [];
			if (keywords.find((key) => key === name)) return true;
			if (name === val?.name) return true;
			if (options?.lowerCase === true) {
				if (keywords.find((key) => lowerCase(key) === lowerCase(name)))
					return true;
				if (lowerCase(name) === lowerCase(val?.name)) return true;
			}
		});

		if (result) {
			return result;
		}

		return null;
	} catch (error) {
		console.log(error);
		return null;
	}
};

export const auth = async (code, settings) => {
	const request = {
		code,
		requestType: 'saml',
	};
	const provider = get(settings, 'provider');
	if (provider) request.provider = provider;
	const auth = await lambda(
		{
			endpoint: 'auth-prod-api',
			variables: request,
		},
		'apiKey'
	);
	return parse(decryptUsingAES256(get(auth, 'token')));
};

// Google sign-in config, fetched ahead of the tap inside the Capacitor shell.
// There the sign-in has to open a new window within the tap's user gesture
// (WebKit drops window.open once an await has passed), so the config must
// already be in hand. Browsers and the RN shell keep fetching on tap.
let warmGoogleAuth = null;

export const warmGoogleSignIn = () => {
	if (!isCapacitorShell() || warmGoogleAuth) return;
	warmGoogleAuth = auth('google')
		.then((value) => rememberWarmGoogleAuth(value))
		.catch(() => {
			warmGoogleAuth = null;
			return null;
		});
};

/** The Google config without awaiting when it is already warm, else null. */
let warmGoogleAuthValue = null;
export const getWarmGoogleAuth = () => warmGoogleAuthValue;
const rememberWarmGoogleAuth = (value) => {
	// Only a real config is kept; a failed warm-up is retried on the tap.
	if (value?.clientId) warmGoogleAuthValue = value;
	return value;
};

/**
 * Google config for the sign-in buttons: synchronous when warm (Capacitor
 * shell), otherwise the usual fetch.
 */
export const googleSignInConfig = async () => {
	if (warmGoogleAuthValue) return warmGoogleAuthValue;
	const config =
		(warmGoogleAuth ? rememberWarmGoogleAuth(await warmGoogleAuth) : null) ||
		(await auth('google'));
	if (!config?.clientId) throw new Error('Google sign-in is not configured');
	return config;
};

export const logout = () => {
	window.location.assign('/logout');
};

const circuitBreakerState = {
	failures: 0,
	lastFailureTime: 0,
	isOpen: false,
	endpoints: {},
	maxFailures: 3,
	resetTimeout: 10_000,
};

export const circuitBreaker = {
	recordSuccess(endpoint) {
		if (circuitBreakerState.endpoints[endpoint]) {
			circuitBreakerState.endpoints[endpoint].failures = 0;
			circuitBreakerState.endpoints[endpoint].isOpen = false;
		}
	},

	recordFailure(endpoint) {
		if (!circuitBreakerState.endpoints[endpoint]) {
			circuitBreakerState.endpoints[endpoint] = {
				failures: 0,
				lastFailureTime: 0,
				isOpen: false,
			};
		}

		const endpointState = circuitBreakerState.endpoints[endpoint];
		endpointState.failures++;
		endpointState.lastFailureTime = Date.now();
		if (endpointState.failures >= circuitBreakerState.maxFailures) {
			endpointState.isOpen = true;
			console.warn(`Circuit breaker opened for endpoint: ${endpoint}`);
		}
	},

	isOpen(endpoint) {
		if (!circuitBreakerState.endpoints[endpoint]) {
			return false;
		}

		const endpointState = circuitBreakerState.endpoints[endpoint];
		// If circuit is open but reset timeout has passed, allow one request through
		if (
			endpointState.isOpen &&
			Date.now() - endpointState.lastFailureTime >
				circuitBreakerState.resetTimeout
		) {
			console.log(
				`Circuit half-open for endpoint: ${endpoint}, allowing test request`
			);
			return false;
		}

		return endpointState.isOpen;
	},

	getState() {
		return { ...circuitBreakerState };
	},
};

// Acquire a fresh Cognito ID-token JWT (refreshing if missing/expired). Extracted from lambda()'s
// cognito branch so other clients (e.g. the CLAUS broker client) can forward the same token.
export const getCognitoAuthHeader = async () => {
	const currentTime = Date.now();
	const authType = localStorage.getItem('authType');
	let { jwtToken, expiresAt } = await getJwtToken(authType);
	if (!jwtToken) jwtToken = await refreshJwtToken(authType);
	if (expiresAt && parseInt(expiresAt) - currentTime < 0)
		jwtToken = await refreshJwtToken(authType);
	return jwtToken;
};

export const lambda = async (body, auth = 'cognito') => {
	const { endpoint } = body;
	if (circuitBreaker.isOpen(endpoint)) {
		console.warn(`Circuit breaker is open for ${endpoint}. Request blocked.`);
		throw new Error(`API request blocked: Too many failures for ${endpoint}`);
	}

	try {
		let response;

		if (auth === 'cognito') {
			const Authorization = await getCognitoAuthHeader();

			const url =
				'https://a0youn41a1.execute-api.us-east-2.amazonaws.com/default/api';

			response = await axios.post(url, body, {
				headers: {
					Authorization,
					'Access-Control-Allow-Origin': '*',
				},
			});
		} else if (auth === 'apiKey') {
			const configMode = getEnvironment() === 'dev' ? 'DEV' : 'PROD';
			const Authorization =
				process.env[`REACT_APP_${configMode}_LAMBDADATACALLSKEY`];
			const url =
				'https://f096mzqyo7.execute-api.us-east-2.amazonaws.com/default/api';
			response = await axios.post(url, body, {
				headers: {
					Authorization,
				},
			});
		}

		circuitBreaker.recordSuccess(endpoint);

		return response?.data;
	} catch (error) {
		// The api lambda rejects every call from a company with disableSite set.
		// Nothing can be done in-session, so end it; the login screen then shows
		// the license message on the next attempt.
		const responseBody = JSON.stringify(error?.response?.data ?? '');
		if (
			error?.response?.status === 401 &&
			responseBody.includes('Unauthorized: Site disabled')
		) {
			window.location.href = `${window.location.origin}/logout`;
			throw error;
		}

		// If this error happens, it's unrecoverable, likely multiple tabs/windows open and someone signed out or cleared cache in one
		if (
			error?.message?.includes('No current user') ||
			error?.response?.data?.message?.includes('No current user')
		)
			window.location.href = `${window.location.origin}/logout`;
		circuitBreaker.recordFailure(endpoint);
		throw error;
	}
};

export const isEmail = (emailAddress) => {
	const sQtext = '[^\\x0d\\x22\\x5c\\x80-\\xff]';
	const sDtext = '[^\\x0d\\x5b-\\x5d\\x80-\\xff]';
	const sAtom =
		'[^\\x00-\\x20\\x22\\x28\\x29\\x2c\\x2e\\x3a-\\x3c\\x3e\\x40\\x5b-\\x5d\\x7f-\\xff]+';
	const sQuotedPair = '\\x5c[\\x00-\\x7f]';
	const sDomainLiteral = '\\x5b(' + sDtext + '|' + sQuotedPair + ')*\\x5d';
	const sQuotedString = '\\x22(' + sQtext + '|' + sQuotedPair + ')*\\x22';
	const sDomain_ref = sAtom;
	const sSubDomain = '(' + sDomain_ref + '|' + sDomainLiteral + ')';
	const sWord = '(' + sAtom + '|' + sQuotedString + ')';
	const sDomain = sSubDomain + '(\\x2e' + sSubDomain + ')*';
	const sLocalPart = sWord + '(\\x2e' + sWord + ')*';
	const sAddrSpec = sLocalPart + '\\x40' + sDomain; // Complete RFC822 email address spec
	const sValidEmail = '^' + sAddrSpec + '$'; // As whole string
	const reValidEmail = new RegExp(sValidEmail);
	return reValidEmail.test(emailAddress);
};

/** Allow passing referral object instead of status in order to get customStatus value. */
const parseStatus = (status) => {
	if (typeof status === 'object') {
		const customStatus = get(status, 'customStatus');
		if (customStatus && get(status, 'status') === 'interviewing') {
			// Only show custom status if the current status is interviewing
			return customStatus;
		}

		return get(status, 'status');
	}

	return status;
};

export const mapReferralStatus = (status, company) => {
	const key = parseStatus(status);
	const referralStatus = get(company, 'referralStatus');
	if (key === 'interviewing' && referralStatus) return referralStatus;
	const customStatuses = parse(get(company, 'referralCustomStatuses'));
	const customStages = parse(get(company, 'stages'));
	const statuses = { ...REFERRAL_STATUS, ...customStatuses };
	let result = statuses[key];
	const customStage = customStages
		? customStages.find((stage) => {
				if (stage[key]) return stage[key];
			})
		: null;
	if (!result && customStage) {
		result = customStage[key];
	} else if (!result && !customStage) {
		Object.entries(REFERRAL_STATUS).map((status) => {
			if (key === status[1]) result = statuses[status[0]];
		});
	}

	return result ?? key;
};

/**
 * Per-company overrides for bonus status display labels.
 *
 * Keyed by company id, then by internal bonus status key (see BONUS_STATUS).
 * Example: a company that wants the "Paid" status shown as "Submitted".
 *
 * NOTE: This is intentionally a hard-coded, company-id keyed lookup (the agreed
 * approach for the current one-off request). If/when this becomes a product
 * feature for multiple companies, swap this map for a company config field
 * (e.g. `company.bonusCustomStatuses`) read inside `mapBonusStatus` — no render
 * site would need to change, since they all go through that helper.
 */
const BONUS_STATUS_LABEL_OVERRIDES = {
	'38afd4b9-e06e-4be3-8bea-15bdea6af4b3': { paid: 'Submitted' }, // Takeda
};

/**
 * Returns the display label for a bonus status, applying any per-company
 * override. Mirrors `mapReferralStatus`.
 *
 * @param {string} status - internal bonus status key (e.g. 'paid', 'earned').
 * @param {object} company - the company object (must include `id`).
 * @param {string} [defaultLabel] - label to fall back to before the shared
 *   BONUS_STATUS map. Pass this where a call site historically rendered
 *   something other than the BONUS_STATUS value (e.g. a capitalized raw key)
 *   so existing, non-overridden labels are preserved exactly.
 * @returns {string} the label to display (callers may still wrap it in `ml`).
 */
export const mapBonusStatus = (status, company, defaultLabel) => {
	const companyId = get(company, 'id');
	const override = get(BONUS_STATUS_LABEL_OVERRIDES, [companyId, status]);
	return override ?? defaultLabel ?? BONUS_STATUS[status] ?? status;
};

export const parseReferralStatus = (status, company) => {
	const statusValue = parseStatus(status);
	if (Object.keys(REFERRAL_STATUS).includes(statusValue)) {
		return statusValue;
	}

	// Return "interviewing" for any custom stage
	const stages = company.stages ? parse(company.stages) : [];
	for (const stage of stages) {
		if (Object.values(stage)[0] === statusValue) {
			return 'interviewing';
		}
	}

	// Return internal status that corresponds to custom status
	const customStatuses = {
		...parse(get(company, 'referralCustomStatuses')),
	};
	return (
		Object.keys(customStatuses).find(
			(key) => customStatuses[key] === statusValue
		) ?? statusValue
	);
};

export const conditionalStatusStyle = (key) => {
	const styles = {
		accepted: { color: 'var(--tan-hide)', fontWeight: 600 },
		declined: { color: 'var(--old-mandarin)', fontWeight: 600 },
		earned: { color: 'var(--fern)', fontWeight: 600 },
		eligible: { color: 'var(--fern)', fontWeight: 600 },
		hired: { color: 'var(--forest-green)', fontWeight: 600 },
		inactive: { color: 'var(--oslo-gray)', fontWeight: 600 },
		ineligible: { color: 'var(--sunset-orange)', fontWeight: 600 },
		interviewing: { color: 'var(--royal-blue)', fontWeight: 600 },
		needsReview: { color: 'var(--mellow-yellow)', fontWeight: 600 },
		noResponse: { color: 'var(--oslo-gray)', fontWeight: 600 },
		notHired: { color: 'var(--sunset-orange)', fontWeight: 600 },
		paid: { color: 'var(--forest-green)', fontWeight: 600 },
		pending: { color: 'var(--tan-hide)', fontWeight: 600 },
		referred: { color: 'var(--deep-blush)', fontWeight: 600 },
		transferred: { color: 'var(--tan-hide)', fontWeight: 600 },
	};

	return styles[key] || { color: 'var(--royal-blue)', fontWeight: 600 };
};

export const sanitize = (string) => {
	const map = {
		'&': '&amp;',
		'<': '&lt;',
		'>': '&gt;',
		'"': '&quot;',
	};
	const reg = /[&<>"]/gi;
	return string.replaceAll(reg, (match) => map[match]);
};

export const desanitize = (string) => {
	// WARNING do NOT use this for a string inside dangerouslySetInnerHTML
	// This is only to display characters in places React will render it as a string
	const map = {
		'&amp;': '&',
		'&lt;': '<',
		'&gt;': '>',
		'&quot;': '"',
		'&#x2F;': '/',
	};

	return Object.keys(map).reduce((acc, entity) => {
		const regex = new RegExp(entity, 'g');
		return acc.replace(regex, map[entity]);
	}, string);
};

export const parseJwt = (token) => {
	try {
		if (!token) {
			return;
		}

		const base64Url = token.split('.')[1]
			? token.split('.')[1]
			: token.split('=')[1];
		const base64 = base64Url
			.replaceAll('-', '+')
			.replaceAll('_', '/')
			.replaceAll('%3D', '=');

		const jsonPayload = decodeURIComponent(
			atob(base64)
				.split('')
				.map(function (c) {
					return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
				})
				.join('')
		);
		return parse(jsonPayload);
	} catch {
		return null;
	}
};

export const addDays = (date, days) => {
	const result = new Date(date);
	result.setDate(result.getDate() + days);
	return result.toISOString();
};

export const subtractDays = (number = 0) => {
	return dayjs().utc().subtract(number, 'days').toISOString();
};

export const getDaysBetweenDates = (from, to) => {
	if (!from || !to) return 0;

	const fromDate = new Date(from.slice(0, 10));
	const toDate = new Date(to.slice(0, 10));

	const diffTime = Math.abs(toDate - fromDate);
	const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

	return diffDays;
};

/** Convert RGB or RGBA to HEX */
const rgba2hex = (rgba) =>
	`#${rgba
		.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+\.{0,1}\d*))?\)$/)
		.slice(1)
		.map((n, i) =>
			(i === 3 ? Math.round(Number.parseFloat(n) * 255) : Number.parseFloat(n))
				.toString(16)
				.padStart(2, '0')
				.replace('NaN', '')
		)
		.join('')}`;

/** Gets default background color, typically rgba(0,0,0,0) */
const getDefaultBackgroundColor = () => {
	const div = document.createElement('div');
	document.head.append(div);
	const color = window.getComputedStyle(div).backgroundColor;
	div.remove();
	return color;
};

/** Gets computed background-color by recursively traversing the DOM until either:
 * A parent element with a background-color is found
 *
 * OR
 *
 * The default background color is returned due to no parent element having a background-color
 * */
const getInheritedBackgroundColor = (element, defaultBackgroundColor) => {
	const { backgroundColor } = window.getComputedStyle(element);

	if (backgroundColor !== defaultBackgroundColor) {
		return backgroundColor;
	}

	if (!element.parentElement) {
		return defaultBackgroundColor;
	}

	return getInheritedBackgroundColor(
		element.parentElement,
		defaultBackgroundColor
	);
};

/** Gets text color for elements with unknown background colors
 *
 * Defaults to transparent due to element not existing in dom on inital call,
 * which means that this funciton will only work correctly for pages with multiple renders.
 *
 * _Luckily_ that isn't a problem for our app 🙃
 */
export const getTextColorFromElementId = (id) => {
	try {
		const element = document.querySelector(`#${id}`);
		if (!element) return COLORS.transparent;

		const defaultBackgroundColor = getDefaultBackgroundColor();
		const computedBackgroundColor = getInheritedBackgroundColor(
			element,
			defaultBackgroundColor
		);
		const isHexColor = computedBackgroundColor.startsWith('#');
		return getTextColorFromBGColor({
			backgroundColor: isHexColor
				? computedBackgroundColor
				: rgba2hex(computedBackgroundColor),
			highContrast: false,
		});
	} catch (error) {
		console.error(error);
		return COLORS.osloGray;
	}
};

export const getTextColorFromBGColor = ({
	backgroundColor,
	threshhold = 200,
	highContrast = true,
}) => {
	// Returns hex string for text color based on background color input
	let rgbColor = backgroundColor;
	if (typeof backgroundColor === 'string') {
		const hexString = backgroundColor.replace(/^#/, '');
		const r = Number.parseInt(hexString.slice(0, 2), 16);
		const g = Number.parseInt(hexString.slice(2, 4), 16);
		const b = Number.parseInt(hexString.slice(4, 6), 16);
		rgbColor = { r, g, b };
	}

	const backgroundBrightness = Math.round(
		(rgbColor.r * 299 + rgbColor.g * 587 + rgbColor.b * 114) / 1000
	);

	if (backgroundBrightness > threshhold) {
		return highContrast ? COLORS.black : COLORS.osloGray;
	}

	return COLORS.white;
};

export const getSetErrorImageURL = async (
	key = 'error-image/erin_lightgray.png'
) => {
	const errorImageURL = localStorage.getItem('errorImageURL');

	if (errorImageURL) {
		return errorImageURL;
	}

	if (key) {
		const url = downloadFromS3(key);
		if (url) {
			localStorage.setItem('errorImageURL', url);
			return url;
		}
	}
};

export const getSize = (size) => {
	if (get(size, 'width') <= 768) return 1;
	if (get(size, 'width') <= 940) return 1;
	if (get(size, 'width') <= 1200) return 2;
	if (get(size, 'width') <= 1600) return 3;
	return 4;
};

export const graphql = async (variables, auth = 'apiKey') => {
	try {
		const configMode = getEnvironment() === 'dev' ? 'DEV' : 'PROD';
		variables.configMode = configMode;
		const response = await lambda(
			{
				endpoint: `graphql`,
				variables,
			},
			auth
		);
		return response;
	} catch (error) {
		console.error(error);
	}
};

export const getISODateRangeUTC = (
	dates = [dayjs(), dayjs().add(1, 'day')]
) => {
	const [start, end] = dates;
	return [
		dayjs(start).utc().startOf('day').toISOString(),
		dayjs(end).utc().endOf('day').toISOString(),
	];
};

export const getUpdatedUserFromSAMLAttributes = ({
	token,
	accountClaim,
	currentUser,
	createUser,
}) => {
	const employeeId =
		accountClaim?.employeeId ||
		token?.['custom:employeeid'] ||
		token?.['custom:userid'] ||
		null;
	const jobClassId = get(token, 'custom:jobClassId');
	const jobClassName = get(token, 'custom:jobClassName');
	const jobFamilyGroupId = get(token, 'custom:jobFamilyGroupId');
	const jobFamilyGroupName = get(token, 'custom:jobFamilyGroupName');
	const jobFamilyId = get(token, 'custom:jobFamilyId');
	const jobFamilyName = get(token, 'custom:jobFamilyName');
	const jobProfileId = get(token, 'custom:jobProfileId');
	const jobProfileName = get(token, 'custom:jobProfileName');
	const managementLevel = get(token, 'custom:managementLevel');
	const customValues = parse(accountClaim?.customValues);
	let subCompanyName = accountClaim?.subCompany || customValues?.Business_Unit; // Business_Unit specific to RTX
	const subCompanies = currentUser?.company?.subCompanies || [];
	let subCompany = findItemByNameAndKeywords(subCompanyName, subCompanies);
	if (!subCompany && token?.['custom:subCompany']) {
		subCompanyName = token?.['custom:subCompany'];
		subCompany = findItemByNameAndKeywords(subCompanyName, subCompanies);
	}

	if (!subCompany && customValues?.Sector) {
		subCompanyName = customValues?.Sector;
		subCompany = findItemByNameAndKeywords(subCompanyName, subCompanies);
	}

	const subCompanyId = accountClaim?.subCompanyId
		? accountClaim.subCompanyId
		: subCompany
			? subCompany.id
			: null;

	let userGroupName =
		accountClaim?.group || customValues?.Primary_Work_Location_Country; // Primary_Work_Location_Country specific to RTX
	const userGroups = currentUser?.company?.userGroups || [];
	let userGroup = findItemByNameAndKeywords(userGroupName, userGroups);
	if (
		!userGroup &&
		(token?.['custom:userGroup'] || token?.['custom:country'])
	) {
		userGroupName = token?.['custom:userGroup'] || token?.['custom:country'];
		userGroup = findItemByNameAndKeywords(userGroupName, userGroups);
	}

	if (!userGroup && !currentUser?.userGroupId) {
		userGroupName = 'Default';
		userGroup = findItemByNameAndKeywords(userGroupName, userGroups);
	}

	const userGroupId = userGroup ? userGroup.id : null;

	let departmentName = accountClaim?.department;
	const departments = currentUser?.company?.departments || [];
	let department = findItemByNameAndKeywords(departmentName, departments);
	if (!department && customValues?.BU) {
		departmentName = customValues?.BU;
		department = findItemByNameAndKeywords(departmentName, departments);
	}

	if (!department && token?.['custom:department']) {
		departmentName = token?.['custom:department'];
		department = findItemByNameAndKeywords(departmentName, departments);
	}

	if (!department && !currentUser?.departmentId) {
		departmentName = 'Other';
		department = findItemByNameAndKeywords(departmentName, departments);
	}

	const departmentId = department ? department.id : null;

	if (accountClaim?.id) currentUser.accountClaimId = accountClaim.id;
	if (departmentId) currentUser.departmentId = departmentId;
	if (employeeId) currentUser.employeeId = employeeId;
	if (jobClassId) currentUser.jobClassId = jobClassId;
	if (jobClassName) currentUser.jobClassName = jobClassName;
	if (jobFamilyGroupId) currentUser.jobFamilyGroupId = jobFamilyGroupId;
	if (jobFamilyGroupName) currentUser.jobFamilyGroupName = jobFamilyGroupName;
	if (jobFamilyId) currentUser.jobFamilyId = jobFamilyId;
	if (jobFamilyName) currentUser.jobFamilyName = jobFamilyName;
	if (jobProfileId) currentUser.jobProfileId = jobProfileId;
	if (jobProfileName) currentUser.jobProfileName = jobProfileName;
	if (managementLevel) currentUser.managementLevel = managementLevel;
	if (subCompanyId) currentUser.subCompanyId = subCompanyId;
	if (userGroupId) currentUser.userGroupId = userGroupId;
	const identities = parse(token?.identities);
	const authMethod = lowerCase(identities?.[0]?.providerType || 'saml');
	let input = {
		authMethod,
		lastLogin: dayjs().toISOString(),
	};
	if (currentUser?.id) input.id = currentUser.id;
	if (accountClaim?.id) input.accountClaimId = accountClaim.id;
	if (departmentId) input.departmentId = departmentId;
	if (employeeId) input.employeeId = employeeId;
	if (jobClassId) input.jobClassId = jobClassId;
	if (jobClassName) input.jobClassName = jobClassName;
	if (jobFamilyGroupId) input.jobFamilyGroupId = jobFamilyGroupId;
	if (jobFamilyGroupName) input.jobFamilyGroupName = jobFamilyGroupName;
	if (jobFamilyId) input.jobFamilyId = jobFamilyId;
	if (jobFamilyName) input.jobFamilyName = jobFamilyName;
	if (jobProfileId) input.jobProfileId = jobProfileId;
	if (jobProfileName) input.jobProfileName = jobProfileName;
	if (managementLevel) input.managementLevel = managementLevel;
	if (subCompanyId) input.subCompanyId = subCompanyId;
	if (userGroupId) input.userGroupId = userGroupId;
	if (createUser) input = { ...createUser, ...input };
	return {
		updatedUser: currentUser,
		input,
	};
};

export const getFilteredNotifications = (notifications = []) => {
	if (!notifications || !Array.isArray(notifications)) {
		return [];
	}

	return notifications.filter((notification) => {
		if (notification.type === 'gdprReferralCreated') {
			return false;
		}

		switch (notification.type) {
			case 'jobCreated': {
				return notification?.job?.title;
			}

			case 'referralCreated':
			case 'referralAccepted':
			case 'referralHired':
			case 'referralSent':
			case 'generalReferral':
			case 'pointsReferralAccepted':
			case 'pointsReferralInterviewing':
			case 'pointsReferralHired': {
				return notification?.referral;
			}

			case 'referralRequested': {
				return notification?.referralRequestedStatus;
			}

			case 'contactImported':
			case 'jobSummaryEmail':
			case 'empMessageCenter':
			case 'socialMediaShare': {
				return notification?.userId || notification?.user?.id;
			}

			default: {
				return false;
			}
		}
	});
};

/**
 * Utility function to fetch form questions in the old format
 * @param {Object} currentUser - The current user object with companyId and userGroupId
 * @param {string} formType - The form type (General, Referral, Acceptance, Interested)
 * @returns {Promise<Array|null>} - Array of questions in old format or null if not found
 */
export const fetchModernFormQuestions = async (
	currentUser,
	formType,
	formConnectionId,
	client
) => {
	try {
		const { companyId } = currentUser;
		const userGroupId = currentUser.userGroupId || null;
		const normalizedFormType = formType.toLowerCase();

		const parseFormData = async (userGroup = null) => {
			const loadedFormData = await loadFormsFromS3(
				companyId,
				userGroup,
				client
			);
			const formByType = loadedFormData?.forms?.[normalizedFormType];
			let formData;

			if (formByType) {
				if (normalizedFormType === 'custom') {
					for (const customForm in formByType) {
						if (
							formByType[customForm].metadata?.formConnectionId ===
							formConnectionId
						) {
							formData = formByType[customForm];
						}
					}
				} else if (formByType?.elements?.length > 0) {
					formData = loadedFormData.forms[normalizedFormType];
				}
			}

			if (!formData && userGroup) {
				formData = await parseFormData();
			}

			return formData;
		};

		const parsedFormData = await parseFormData(userGroupId);
		if (formConnectionId) {
			return parsedFormData;
		} else {
			return convertModernToOldFormat({
				formData: parsedFormData,
				companyId: companyId,
				formType: normalizedFormType,
			});
		}
	} catch {
		return [];
	}
};

/**
 * Fetches ALL custom forms that share a given formConnectionId.
 * Uses the getCompanyForms query with returnArray: true to get
 * the full form data, then filters the custom forms array.
 */
export const fetchFormsByConnectionId = async (
	currentUser,
	formConnectionId,
	client
) => {
	try {
		const { companyId } = currentUser;

		const GET_COMPANY_FORMS = gql`
			query GetCompanyForms(
				$companyId: ID!
				$formType: CompanyFormType
				$formConnectionId: String
				$returnArray: Boolean
			) {
				getCompanyForms(
					companyId: $companyId
					formType: $formType
					formConnectionId: $formConnectionId
					returnArray: $returnArray
				) {
					companyId
					formConnectionId
					formData
					formType
				}
			}
		`;

		const { data } = await client.query({
			query: GET_COMPANY_FORMS,
			variables: {
				companyId,
				formType: 'custom',
				formConnectionId,
				returnArray: true,
			},
			fetchPolicy: 'network-only',
		});

		if (data?.getCompanyForms?.formData) {
			const parsed = JSON.parse(data.getCompanyForms.formData);
			const customForms = parsed?.forms?.custom;

			if (!Array.isArray(customForms)) {
				return [];
			}

			return customForms.filter(
				(form) => form?.metadata?.formConnectionId === formConnectionId
			);
		}

		return [];
	} catch {
		return [];
	}
};

/**
 * Helper function to load forms from S3
 * @param {string} companyId - The company ID
 * @param {string|null} userGroupId - The user group ID or null for main company
 * @returns {Promise<Object|null>} - The loaded form data or null if not found
 */
async function loadFormsFromS3(companyId, userGroupId = null, client) {
	const GET_COMPANY_FORMS = gql`
		query GetCompanyForms(
			$companyId: ID!
			$userGroupId: ID
			$formType: CompanyFormType
			$formConnectionId: String
			$returnArray: Boolean
		) {
			getCompanyForms(
				companyId: $companyId
				userGroupId: $userGroupId
				formType: $formType
				formConnectionId: $formConnectionId
				returnArray: $returnArray
			) {
				companyId
				formConnectionId
				formData
				lastModified
				formType
				userGroupId
				metadata {
					totalForms
					updatedAt
					version
				}
			}
		}
	`;
	try {
		const { data } = await client.query({
			query: GET_COMPANY_FORMS,
			variables: {
				companyId,
				userGroupId,
			},
		});

		if (data?.getCompanyForms?.formData) {
			return JSON.parse(data.getCompanyForms.formData);
		} else {
			return null;
		}
	} catch (error) {
		console.log('Form file not found or error loading:', error);
		return null;
	}
}

/**
 * Convert modern form format to old format
 * @param {Object} formData - The modern form data
 * @param {string} companyId - The company ID
 * @param {string} formType - The form type
 * @returns {Array} - Array of questions in old format
 */
export function convertModernToOldFormat({ formData, companyId, formType }) {
	const oldFormatQuestions = [];

	const isCandidate = formType === 'acceptance';
	const isGeneral = formType === 'general';
	const isInterested = formType === 'interested';

	const metadataObj = {};

	if (formData.metadata) {
		if (formData.metadata.askForResume !== undefined) {
			metadataObj['Ask for resume attachment'] = Boolean(
				formData.metadata.askForResume
			);
		}

		if (formData.metadata.resumeRequired !== undefined) {
			metadataObj['Resume required'] = Boolean(
				formData.metadata.resumeRequired
			);
		}

		if (
			formType === 'Interested' &&
			formData.metadata.showUserEmail !== undefined
		) {
			metadataObj["Show User's Email Address"] = Boolean(
				formData.metadata.showUserEmail
			);
		}
	}

	oldFormatQuestions.push({
		id: `q_metadata_${formType.toLowerCase()}`,
		active: true,
		companyId,
		isCandidate,
		isGeneral,
		isInterested,
		sortOrder: 0,
		questions: metadataObj,
	});

	if (
		formData.elements &&
		Array.isArray(formData.elements) &&
		formData.elements.length > 0
	) {
		formData.elements.forEach((element, index) => {
			const questionId = element.name?.startsWith('field_')
				? element.name.replace('field_', 'q_')
				: element.name?.startsWith('q_')
					? element.name
					: `q_${Date.now()}_${index}`;

			const fieldName = element.name?.startsWith('field_')
				? element.name.replace('field_', '')
				: element.name?.startsWith('q_')
					? element.name.replace('q_', '')
					: `field_${Date.now()}_${index}`;

			let questionObj = {};

			switch (element.type) {
				case 'input': {
					questionObj = {
						id: element.id || questionId,
						element: 'TextInput',
						label: element.label,
						text: 'Text Input',
						required: element.required || false,
						placeholder: element.placeholder || 'Enter text',
						canHaveAnswer: true,
						canHavePageBreakBefore: true,
						canHaveAlternateForm: true,
						canHaveDisplayHorizontal: false,
						canHaveOptionCorrect: false,
						canHaveOptionValue: false,
						canPopulateFromApi: true,
						field_name: `text_input_${fieldName}`,
					};
					break;
				}

				case 'textarea': {
					questionObj = {
						id: element.id || questionId,
						element: 'TextArea',
						label: element.label,
						text: 'Multi-line Input',
						required: element.required || false,
						placeholder: element.placeholder || 'Enter text',
						canHaveAnswer: true,
						canHavePageBreakBefore: true,
						canHaveAlternateForm: true,
						canHaveDisplayHorizontal: false,
						canHaveOptionCorrect: false,
						canHaveOptionValue: false,
						canPopulateFromApi: true,
						field_name: `text_area_${fieldName}`,
					};
					break;
				}

				case 'select': {
					questionObj = {
						id: element.id || questionId,
						element: 'Dropdown',
						text: element.label || 'Dropdown',
						required: element.required || false,
						placeholder: element.placeholder || 'Select an option',
						canHaveAnswer: true,
						canHavePageBreakBefore: true,
						canHaveAlternateForm: true,
						canHaveDisplayHorizontal: true,
						canHaveOptionCorrect: true,
						canHaveOptionValue: true,
						canPopulateFromApi: true,
						field_name: `dropdown_${fieldName}`,
						label: element.label || 'Select',
						options: (element.options || []).map((opt, optIndex) => ({
							value:
								opt.value || opt.label.toLowerCase().replaceAll(/\s+/g, '_'),
							text: opt.label,
							key: opt.key || `dropdown_option_${optIndex}`,
						})),
					};
					break;
				}

				case 'label': {
					questionObj = {
						id: element.id || questionId,
						element: 'Label',
						text: 'Label',
						content: element.content || '',
						static: true,
						required: false,
						canHaveAnswer: false,
						canHavePageBreakBefore: true,
						canHaveAlternateForm: true,
						canHaveDisplayHorizontal: true,
						canHaveOptionCorrect: true,
						canHaveOptionValue: true,
						canPopulateFromApi: true,
					};
					break;
				}

				case 'linebreak': {
					questionObj = {
						id: element.id || questionId,
						element: 'LineBreak',
						text: '',
						required: false,
						static: true,
						canHaveAnswer: false,
						canHavePageBreakBefore: true,
						canHaveAlternateForm: true,
						canHaveDisplayHorizontal: false,
						canHaveOptionCorrect: false,
						canHaveOptionValue: false,
						canPopulateFromApi: false,
					};
					break;
				}

				default: {
					console.warn(`Unknown element type: ${element.type}`);
				}
			}
			if (questionObj) {
				oldFormatQuestions.push({
					id: questionId,
					active: true,
					companyId,
					isCandidate,
					isGeneral,
					isInterested,
					sortOrder: element.sortOrder || index + 1,
					questions: JSON.stringify(questionObj),
				});
			}
		});
	}

	return oldFormatQuestions;
}

/**
 * Brightens a hex color by converting to HSL, adjusting lightness, and converting back to hex
 * @param {string} hexColor - Hex color string (with or without #)
 * @param {number} factor - Amount to brighten (0-100), represents percentage increase in lightness
 * @returns {string} Brightened hex color
 */
export const brightenHexColor = (hexColor, factor) => {
	hexColor = hexColor.replace(/^#/, '');

	const r = Number.parseInt(hexColor.slice(0, 2), 16) / 255;
	const g = Number.parseInt(hexColor.slice(2, 4), 16) / 255;
	const b = Number.parseInt(hexColor.slice(4, 6), 16) / 255;

	const max = Math.max(r, g, b);
	const min = Math.min(r, g, b);

	let h;
	let s;
	let l = (max + min) / 2;

	if (max === min) {
		h = s = 0;
	} else {
		const d = max - min;
		s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

		switch (max) {
			case r: {
				h = (g - b) / d + (g < b ? 6 : 0);
				break;
			}

			case g: {
				h = (b - r) / d + 2;
				break;
			}

			case b: {
				h = (r - g) / d + 4;
				break;
			}
		}

		h /= 6;
	}

	l = Math.min(1, l + factor / 100);

	let r1;
	let g1;
	let b1;

	if (s === 0) {
		// Achromatic (gray)
		r1 = g1 = b1 = l;
	} else {
		const hue2rgb = (p, q, t) => {
			if (t < 0) t += 1;
			if (t > 1) t -= 1;
			if (t < 1 / 6) return p + (q - p) * 6 * t;
			if (t < 1 / 2) return q;
			if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
			return p;
		};

		const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
		const p = 2 * l - q;

		r1 = hue2rgb(p, q, h + 1 / 3);
		g1 = hue2rgb(p, q, h);
		b1 = hue2rgb(p, q, h - 1 / 3);
	}

	const toHex = (x) => {
		const hex = Math.round(x * 255).toString(16);
		return hex.length === 1 ? '0' + hex : hex;
	};

	return `#${toHex(r1)}${toHex(g1)}${toHex(b1)}`;
};

export function makeStringFilenameSafe(str) {
	// First remove spaces
	const noSpaces = str.replaceAll(/\s/g, '');

	// Keep only alphanumeric, hyphen, and underscore characters
	return noSpaces.replaceAll(/[^\w\-]/g, '');
}

export const base64ToBlob = (base64String, contentType = 'image/png') => {
	if (base64String.startsWith('data:')) {
		base64String = base64String.split(',')[1];
	}

	const byteCharacters = atob(base64String);
	const byteArrays = [];

	for (let offset = 0; offset < byteCharacters.length; offset += 512) {
		const slice = byteCharacters.slice(offset, offset + 512);

		const byteNumbers = Array.from({ length: slice.length });
		for (let i = 0; i < slice.length; i++) {
			byteNumbers[i] = slice.charCodeAt(i);
		}

		const byteArray = new Uint8Array(byteNumbers);
		byteArrays.push(byteArray);
	}

	return new Blob(byteArrays, { type: contentType });
};

export const uploadPngBase64ToS3 = async (
	base64String,
	key,
	bucket,
	type = 'png'
) => {
	// Convert base64 to Blob
	let blob;
	blob =
		type === 'png'
			? base64ToBlob(base64String)
			: new Blob([base64String], { type: 'image/svg+xml' });

	// Create a File object (more metadata than Blob)
	const file = new File([blob], key, { type: 'image/png' });

	return uploadToS3(file, key, bucket);
};

/**
 * True when the page is hosted inside an ERIN mobile shell (ERIN-8561): the
 * React Native WebView (window.ERINShell), the Capacitor iOS shell (user
 * agent) or the Android Trusted Web Activity (?shell=1). See src/_shared/shell.js.
 */
export { isMobileShell } from 'src/_shared/shell.js';

/**
 * Starts a Cognito hosted-UI sign-in (Google or a SAML provider).
 *
 * In the Capacitor shell the request is tagged state=erinshell. The shell
 * keeps Cognito in the WebView but hands any other host (Google, which
 * forbids OAuth inside embedded WebViews, or a SAML provider) to Safari, so
 * the sign-in may finish there; the callback page, seeing the tag outside
 * the shell, then bounces the code back to the app on erinapp://. The RN
 * shell intercepts Google itself, and the Android TWA is Chrome, so both
 * just navigate.
 */
export const startHostedUiSignIn = (url) => {
	if (isCapacitorShell()) {
		window.location.href = `${url}${url.includes('?') ? '&' : '?'}state=erinshell`;
		return;
	}

	window.location.href = url;
};

/**
 * Sends a message to the ERIN mobile shell (see WebShell.js there for the
 * message types). Returns false outside the shell.
 */
export const postToShell = (message) => {
	try {
		if (!isMobileShell() || !window.ReactNativeWebView?.postMessage) {
			return false;
		}

		window.ReactNativeWebView.postMessage(JSON.stringify(message));
		return true;
	} catch {
		return false;
	}
};
